import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import { api } from "@/lib/api";

/* ═════════════════════════════════════════════════════════════════════
   Global public footer — 5 groups (Product · Solutions · Trust · Legal
   · Company) + legal-entity fields, all sourced from
   /api/public-content/{footer-links, legal-entity}.
   Uses the dark-navy palette to match Landing v2.
   ═════════════════════════════════════════════════════════════════════ */

const DEFAULT_LINKS = {
    product:   [["Features", "/features"], ["Pricing", "/pricing"], ["Demo", "/demo"],
                ["Changelog", "/changelog"], ["Roadmap", "/roadmap"]],
    solutions: [["Individuals", "/solutions/individuals"], ["Families", "/solutions/families"],
                ["Business Owners", "/solutions/business-owners"], ["Finance Teams", "/solutions/finance-teams"],
                ["Accountants", "/solutions/accountants"]],
    trust:     [["Trust & Legal Centre", "/trust"], ["Security Centre", "/security"], ["Privacy Centre", "/privacy-centre"],
                ["Subprocessors", "/subprocessors"], ["System Status", "/status"],
                ["Report a Vulnerability", "/security/report-vulnerability"]],
    legal:     [["Terms of Service", "/terms"], ["Privacy Policy", "/privacy"],
                ["Cookie Policy", "/cookies"], ["Subscription & Billing Terms", "/billing-terms"],
                ["Refund & Cancellation Policy", "/refund-policy"],
                ["Acceptable Use Policy", "/acceptable-use"],
                ["Financial Disclaimer", "/financial-disclaimer"],
                ["AI & Automated Insights", "/ai-disclosure"],
                ["Regional Privacy", "/regional-privacy"],
                ["Grievance Redressal", "/grievance"],
                ["Accessibility", "/accessibility"]],
    company:   [["About", "/about"], ["Contact", "/contact"], ["Support", "/support"],
                ["Press Kit", "/press-kit"]],
};

export default function PublicFooter() {
    const { user } = useAuth();
    const { t } = useLocale();
    const [links, setLinks] = useState(DEFAULT_LINKS);
    const [entity, setEntity] = useState(null);

    useEffect(() => {
        api.get("/public-content/footer-links").then((r) => {
            const d = r.data || {};
            // Backend stores keys as lowercase groups mapping to [[label, href], ...]
            setLinks((prev) => ({ ...prev, ...d }));
        }).catch(() => {});
        api.get("/public-content/legal-entity").then((r) => setEntity(r.data)).catch(() => {});
    }, []);

    return (
        <footer className="pf-root" data-testid="public-footer">
            <style>{styles}</style>
            <div className="pf-container">
                <div className="pf-grid">
                    <div className="pf-brandcol">
                        <div className="pf-brandrow">
                            <span className="pf-brand">VAULT</span>
                            <span className="pf-tag">by PD</span>
                        </div>
                        <p className="pf-tagline">
                            <span className="pf-tagline-lead">{t("footer.tagline_1")}</span>
                            <span className="pf-tagline-body">{t("footer.tagline_2")}</span>
                        </p>
                        {!user && (
                            <Link to="/signup" className="pf-cta" data-testid="footer-signup">
                                {t("footer.cta_signup")} <span aria-hidden="true">→</span>
                            </Link>
                        )}
                        {user && (
                            <Link to="/dashboard" className="pf-cta" data-testid="footer-dashboard">
                                {t("footer.cta_dashboard")} <span aria-hidden="true">→</span>
                            </Link>
                        )}
                    </div>

                    <FooterCol title={t("footer.group.product")}   items={links.product}   />
                    <FooterCol title={t("footer.group.solutions")} items={links.solutions} />
                    <FooterCol title={t("footer.group.trust")}     items={links.trust}     />
                    <FooterCol title={t("footer.group.legal")}     items={links.legal}     />
                    <FooterCol title={t("footer.group.company")}   items={links.company}   />
                </div>

                <div className="pf-legal">
                    <div className="pf-legal-cell">
                        <div className="pf-legal-lbl">Support</div>
                        <a href={`mailto:${entity?.support_email || "support@vaultbypd.com"}`}
                           className="pf-legal-val">{entity?.support_email || "support@vaultbypd.com"}</a>
                    </div>
                    <div className="pf-legal-cell">
                        <div className="pf-legal-lbl">Privacy</div>
                        <a href={`mailto:${entity?.privacy_email || "privacy@vaultbypd.com"}`}
                           className="pf-legal-val">{entity?.privacy_email || "privacy@vaultbypd.com"}</a>
                    </div>
                    <div className="pf-legal-cell">
                        <div className="pf-legal-lbl">Security</div>
                        <a href={`mailto:${entity?.security_email || "security@vaultbypd.com"}`}
                           className="pf-legal-val">{entity?.security_email || "security@vaultbypd.com"}</a>
                    </div>
                    {entity?.legal_entity_name && (
                        <div className="pf-legal-cell">
                            <div className="pf-legal-lbl">Registered entity</div>
                            <div className="pf-legal-val">{entity.legal_entity_name}</div>
                        </div>
                    )}
                </div>

                <div className="pf-regulatory" data-testid="footer-regulatory-strip">
                    <span>VAULT is a personal financial information management platform.
                    Not a bank, broker or investment adviser.{" "}
                    <Link to="/financial-disclaimer" className="pf-regulatory-link">Read the Financial Disclaimer →</Link></span>
                </div>

                <div className="pf-copybar">
                    <div className="pf-copy">
                        {entity?.copyright || `© ${new Date().getFullYear()} VAULT · Created by PD`}
                    </div>
                    <div className="pf-copy-links">
                        <button type="button" onClick={() => window.dispatchEvent(new CustomEvent("vault:cookie-preferences"))} className="pf-copy-link" data-testid="footer-cookie-prefs">
                            Cookie Preferences
                        </button>
                        <Link to="/privacy" className="pf-copy-link">Privacy</Link>
                        <Link to="/terms" className="pf-copy-link">Terms</Link>
                        <Link to="/accessibility" className="pf-copy-link">Accessibility</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}

function FooterCol({ title, items }) {
    return (
        <div className="pf-col">
            <div className="pf-col-title">{title}</div>
            <ul className="pf-col-list">
                {(items || []).map(([label, href]) => (
                    <li key={label}>
                        {href.startsWith("http") ? (
                            <a href={href} target="_blank" rel="noreferrer" className="pf-col-link">{label}</a>
                        ) : (
                            <Link to={href} className="pf-col-link">{label}</Link>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
}

const styles = `
.pf-root { background: #05080F; color: #F6F3EC; border-top: 1px solid rgba(246,243,236,0.06); }
.pf-container { max-width: 1200px; margin: 0 auto; padding: 64px 28px 40px; }
.pf-grid { display: grid; grid-template-columns: 1.4fr repeat(5, 1fr); gap: 40px; }
@media (max-width: 1024px) { .pf-grid { grid-template-columns: 1fr 1fr 1fr; } .pf-brandcol { grid-column: 1 / -1; margin-bottom: 20px; } }
@media (max-width: 640px)  { .pf-grid { grid-template-columns: 1fr 1fr; } }

.pf-brand    { font-weight: 900; font-size: 22px; letter-spacing: 0.22em; color: #F6F3EC; line-height: 1; }
.pf-tag      { font-weight: 600; font-size: 9.5px; letter-spacing: 0.32em; text-transform: uppercase; color: rgba(246,243,236,0.4); line-height: 1; padding-bottom: 2px; }
.pf-brandrow { display: flex; align-items: baseline; gap: 12px; padding-bottom: 22px; border-bottom: 1px solid rgba(246,243,236,0.08); }
.pf-tagline  { margin-top: 22px; display: flex; flex-direction: column; gap: 8px; max-width: 260px; }
.pf-tagline-lead { font-size: 14px; font-weight: 600; color: rgba(246,243,236,0.92); line-height: 1.45; letter-spacing: -0.005em; }
.pf-tagline-body { font-size: 12.5px; font-weight: 400; color: rgba(246,243,236,0.5); line-height: 1.6; letter-spacing: 0.005em; }
.pf-cta      { display: inline-flex; align-items: center; gap: 6px; margin-top: 24px; font-size: 12.5px; font-weight: 600; color: #C6A86B; letter-spacing: 0.02em; text-transform: none; transition: gap .15s ease, color .15s ease; }
.pf-cta:hover { color: #d3b978; gap: 10px; }

.pf-col-title { font-size: 10.5px; font-weight: 700; letter-spacing: 0.22em; text-transform: uppercase; color: rgba(246,243,236,0.45); margin-bottom: 14px; }
.pf-col-list { display: flex; flex-direction: column; gap: 10px; }
.pf-col-link { font-size: 13px; color: rgba(246,243,236,0.72); transition: color .15s; }
.pf-col-link:hover { color: #F6F3EC; }

.pf-legal { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin-top: 48px; padding-top: 32px; border-top: 1px solid rgba(246,243,236,0.06); }
@media (max-width: 640px) { .pf-legal { grid-template-columns: 1fr 1fr; } }
.pf-legal-lbl { font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase; color: rgba(246,243,236,0.45); font-weight: 700; margin-bottom: 6px; }
.pf-legal-val { font-size: 13px; color: rgba(246,243,236,0.85); }
a.pf-legal-val:hover { color: #F6F3EC; text-decoration: underline; }

.pf-copybar { display: flex; align-items: center; justify-content: space-between; margin-top: 32px; padding-top: 20px; border-top: 1px solid rgba(246,243,236,0.06); flex-wrap: wrap; gap: 12px; }
.pf-copy { font-size: 12px; color: rgba(246,243,236,0.45); }
.pf-copy-links { display: flex; gap: 20px; }
.pf-copy-link { font-size: 12px; color: rgba(246,243,236,0.6); background: none; border: none; padding: 0; cursor: pointer; }
.pf-copy-link:hover { color: #F6F3EC; }
.pf-regulatory { margin: 24px 0 20px; padding: 12px 18px; border-radius: 10px; background: rgba(246,243,236,0.03); border: 1px solid rgba(246,243,236,0.07); font-size: 11.5px; line-height: 1.6; color: rgba(246,243,236,0.55); text-align: center; }
.pf-regulatory-link { color: #C6A86B; font-weight: 600; margin-left: 4px; transition: color .15s ease; }
.pf-regulatory-link:hover { color: #d3b978; }
`;
